/*
 * Trust Fabric: who may change the customer's boxes, under which key cards,
 * and the signed record of what they did, as one canvas to zoom into. The
 * customer's key, its agents, its boxes and the Weavian gateways that
 * countersign them; a box as the path a change takes (who may ask, the guard,
 * the trial, the logbook, its gateway); a stage's rows; a row's signed fact.
 * Everything drawn rests on a fact the controller holds: pointing at a thing
 * or a line says who signed it and when, and its fact opens from there.
 *
 * Fed by GET /api/v1/fabric (server/pkg/fabric) every 30 s; a fact is read by
 * name from GET /api/v1/facts and shown only when its digest is the one cited.
 * The model and the drawing are TrustFabricLayout.js; this file measures the
 * screen, zooms, fetches and says where you are. An older server answers 404
 * and gets the console's feature-unavailable notice.
 *
 * ! file components/TrustFabric.js
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import useVisibleInterval from "../hooks/useVisibleInterval";
import usePhoneLayout from "../hooks/usePhoneLayout";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { FONT, asOf, at, atS, buildFabric, factRows, factTitle, forgetMeasures, heldRows, hm, isHeld, layoutFabric, list, notDrawnLine, provWords, r2, refKey, refOf } from "./TrustFabricLayout";
import "./TrustFabric.css";

const READ_MS = 30000;
const RETRY_MS = 30000;
const CUSTOMER_KEY = "nabx.trustFabric.customer";
const HASH = "fabric";
const HIDDEN = { display: "none" };
const MIN_H = 420;

const remembered = () => { try { return window.localStorage.getItem(CUSTOMER_KEY) || ""; } catch { return ""; } };
const remember = (id) => { try { window.localStorage.setItem(CUSTOMER_KEY, id); } catch { /* a browser that keeps nothing shows the customer picked until reload */ } };

/* A fact as GET /api/v1/facts answers it: its envelope's payload read, and
   the digest that names it, SHA-256 over what its signatures cover. */
const bytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
async function digestOf(s) {
  if (!window.crypto?.subtle) throw new Error("this page cannot check a fingerprint, since it is not served over HTTPS");
  const body = bytes(s.payload), head = new TextEncoder().encode(`DSSEv1 ${s.payloadType.length} ${s.payloadType} ${body.length} `), all = new Uint8Array(head.length + body.length);
  all.set(head);
  all.set(body, head.length);
  return [...new Uint8Array(await window.crypto.subtle.digest("SHA-256", all))].map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function readFact(serverApiUrl, ref) {
  const res = await fetchWithTimeout(serverApiUrl(`/facts?prefix=${encodeURIComponent(ref.name)}&limit=50`), { credentials: "same-origin" }).catch(() => null);
  if (!res) return { error: "The Weavian server did not answer, so this fact is not shown." };
  const out = await res.json().catch(() => null);
  if (!res.ok) return { error: out?.error || `The server refused to show it (HTTP ${res.status}).` };
  for (const s of out?.facts || []) {
    const fact = JSON.parse(new TextDecoder().decode(bytes(s.payload)));
    if (fact.name !== ref.name) continue;
    const digest = await digestOf(s);
    if (!ref.digest || digest === ref.digest) return { fact, digest };
  }
  return { error: "The controller holds no fact by this name and fingerprint." };
}

const smooth = (a, b, x) => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const lerp = (a, b, t) => a + (b - a) * t;
const ease = (t) => ((t *= 2) <= 1 ? t * t * t : (t -= 2) * t * t + 2) / 2;
/* van Wijk and Nuij's smooth zoom and pan, as d3.interpolateZoom: from one
   view to another, each its centre and width in world units. */
function flyPath([ux0, uy0, w0], [ux1, uy1, w1]) {
  const rho = Math.SQRT2, dx = ux1 - ux0, dy = uy1 - uy0, d2 = dx * dx + dy * dy;
  if (d2 < 1e-12) { const S = Math.log(w1 / w0) / rho; return { S, at: (t) => [ux0 + t * dx, uy0 + t * dy, w0 * Math.exp(rho * t * S)] }; }
  const d1 = Math.sqrt(d2), b0 = (w1 * w1 - w0 * w0 + 4 * d2) / (4 * w0 * d1), b1 = (w1 * w1 - w0 * w0 - 4 * d2) / (4 * w1 * d1);
  const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0), r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1), S = (r1 - r0) / rho;
  return { S, at: (t) => { const s = t * S, c0 = Math.cosh(r0), u = (w0 / (2 * d1)) * (c0 * Math.tanh(rho * s + r0) - Math.sinh(r0)); return [ux0 + u * dx, uy0 + u * dy, (w0 * c0) / Math.cosh(rho * s + r0)]; } };
}
const sayOf = (nd) => nd.say || [nd.title, nd.body].filter(Boolean).map((s) => (/[.!?]$/.test(s) ? s : `${s}.`)).join(" ");
const edgeWords = (x) => ({ issued: `Key cards from your key to ${x.z.label}`, mandate: `${x.a.label}’s key card for ${x.z.label}`, witness: `${x.z.label}’s countersignatures of ${x.a.label}’s logbook`, spare: `${x.z.label} countersigned ${x.a.label}’s logbook before` }[x.kind]);
/* The height from an element's top to the foot of the area it scrolls in. */
function toFoot(el) {
  let s = el.parentElement;
  while (s && !/auto|scroll/.test(window.getComputedStyle(s).overflowY)) s = s.parentElement;
  const top = el.getBoundingClientRect().top;
  return s ? s.clientHeight - (top - s.getBoundingClientRect().top + s.scrollTop) : window.innerHeight - top - window.scrollY;
}

/* The canvas's motion: built once, read from the refs it is given. */
function createEngine(dom, set, live) {
  const E = { T: { x: 0, y: 0, k: 1 }, SW: 0, SH: 0, FITK: 1, kMax: 4, world: null, mini: { w: 0, h: 0, on: false }, focus: null, target: "",
    free: false, fly: 0, raf: 0, shown: new Set(), ptr: new Map(), down: null, dragged: false, hashT: 0, home: null };
  const root = () => live.current.model.root;
  const node = (id) => live.current.model.NODES.get(id);
  const rm = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const openOf = (nd) => (nd.L ? smooth(8.5, 11, 13 * nd.IS * E.T.k) : 0);
  const queue = () => { if (!E.raf) E.raf = requestAnimationFrame(frame); };
  const setA = (nd, el, k, v) => { if (el && nd._c[k] !== v) { el.setAttribute(k.split(":")[0], v); nd._c[k] = v; } };
  const clampK = (k) => Math.max(E.FITK * 0.5, Math.min(E.kMax, k));
  function constrain(t) {
    const w = E.world, dx0 = -t.x / t.k - (w.x - w.w), dx1 = (E.SW - t.x) / t.k - (w.x + 2 * w.w), dy0 = -t.y / t.k - (w.y - w.h), dy1 = (E.SH - t.y) / t.k - (w.y + 2 * w.h);
    const ax = dx1 > dx0 ? (dx0 + dx1) / 2 : Math.min(0, dx0) || Math.max(0, dx1), ay = dy1 > dy0 ? (dy0 + dy1) / 2 : Math.min(0, dy0) || Math.max(0, dy1);
    return { k: t.k, x: t.x + t.k * ax, y: t.y + t.k * ay };
  }
  const setT = (t) => { E.T = E.world ? constrain(t) : t; set.tag(null); queue(); };
  const zoomAt = (px, py, k) => { const k1 = clampK(k), wx = (px - E.T.x) / E.T.k, wy = (py - E.T.y) / E.T.k; setT({ x: px - wx * k1, y: py - wy * k1, k: k1 }); };
  const stopFly = () => { if (E.fly) cancelAnimationFrame(E.fly); E.fly = 0; };

  /* Zooming to a thing shows its inside at reading size: whole when it fits,
     from the top when it is taller, clear of the overview map's corner. */
  function fitT(nd) {
    const phone = E.SW < 600, p = 16, top = nd === root(), r = top ? E.world : nd.L ? nd.irPad : nd.r, read = top ? 1 : 1 / (nd.L ? nd.IS : nd.S);
    const side = !phone && !top, mw = E.mini.w + 24, mh = E.mini.h + 24, vw = E.SW - 2 * p, vh = E.SH - 2 * p;
    const kA = Math.min((vw - (side ? 2 * mw : 0)) / r.w, vh / r.h), kB = Math.min(vw / r.w, (vh - (side ? mh : 0)) / r.h), under = side && kB > kA, kf = Math.max(kA, kB);
    // The whole picture opens at most at its reading size.
    let k = top ? Math.min(phone ? vw / r.w : kf, 1) : Math.max(Math.min(kf, read * 1.12), read * (phone ? 1 : 0.9));
    k = Math.min(k, (vw - (side && !under ? 2 * mw : 0)) / r.w);
    const w = r.w * k, ht = r.h * k, x = E.SW / 2 - w / 2;
    let y = ht <= vh ? p + (vh - ht) / 2 : p;
    if (under && y + ht > E.SH - p - mh && x + w > E.SW - p - mw) y = Math.max(p, E.SH - p - mh - ht);
    return { x: x - r.x * k, y: y - r.y * k, k };
  }
  function go(nd, instant) {
    if (!nd || !E.world) return;
    E.free = false;
    E.target = nd.id;
    if (nd.fact) live.current.need(nd.fact);
    const tr = fitT(nd);
    stopFly();
    if (instant || rm()) { setT(tr); return; }
    const w = Math.max(E.SW, E.SH), T = E.T, c0 = [(E.SW / 2 - T.x) / T.k, (E.SH / 2 - T.y) / T.k, w / T.k], c1 = [(E.SW / 2 - tr.x) / tr.k, (E.SH / 2 - tr.y) / tr.k, w / tr.k];
    const path = flyPath(c0, c1), dur = Math.max(350, Math.min(1100, Math.abs(path.S) * 850)), t0 = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur), [x, y, ww] = path.at(ease(t)), k = w / ww;
      E.T = { x: E.SW / 2 - x * k, y: E.SH / 2 - y * k, k };
      set.tag(null);
      queue();
      if (t < 1) { E.fly = requestAnimationFrame(step); return; }
      E.fly = 0;
      setT(fitT(node(E.target) || nd));
    };
    E.fly = requestAnimationFrame(step);
  }
  /* The deepest thing open in the middle of the view. */
  function focusOf() {
    const cx = E.SW / 2, cy = E.SH / 2;
    let nd = root();
    for (;;) {
      const c = nd.kids.find((k) => { if (!k.r || openOf(k) < 0.5) return false; const f = k.irPad, x = E.T.x + E.T.k * f.x, y = E.T.y + E.T.k * f.y; return x <= cx && x + E.T.k * f.w >= cx && y <= cy && y + E.T.k * f.h >= cy; });
      if (!c) return nd;
      nd = c;
    }
  }
  const backOut = () => { const f = focusOf(); go(f === root() || !f.parent ? root() : f.parent); };
  const zoomBy = (f) => {
    stopFly();
    E.free = true;
    const k0 = E.T.k, k1 = clampK(k0 * f), t0 = performance.now();
    if (rm()) { zoomAt(E.SW / 2, E.SH / 2, k1); return; }
    const step = (now) => { const t = Math.min(1, (now - t0) / 220); zoomAt(E.SW / 2, E.SH / 2, k0 * (k1 / k0) ** ease(t)); E.fly = t < 1 ? requestAnimationFrame(step) : 0; };
    E.fly = requestAnimationFrame(step);
  };

  function frame() {
    E.raf = 0;
    const { world, edges, dots, dot, mv } = dom;
    if (!E.world || !world.current) return;
    const T = E.T, R = root();
    world.current.setAttribute("transform", `translate(${r2(T.x)} ${r2(T.y)}) scale(${T.k})`);
    const s = T.k / 2 ** Math.floor(Math.log2(T.k));
    dots.current.setAttribute("patternTransform", `translate(${r2(T.x)} ${r2(T.y)}) scale(${r2(s)})`);
    dot.current.setAttribute("r", r2(0.9 / s));
    const ea = 1 - smooth(E.FITK * 1.9, E.FITK * 3.2, T.k);
    edges.current.setAttribute("opacity", r2(ea));
    edges.current.style.display = ea < 0.02 ? "none" : "";
    const F = E.focus && E.focus !== R ? E.focus : null, mm = E.mini.on ? [E.SW - 16 - E.mini.w - 8, E.SH - 16 - E.mini.h - 8] : null, fast = rm();
    let fd = 0, more = false;
    for (let q = F; q && q.parent; q = q.parent) fd++;
    const now = new Set();
    (function visit(nd, a) {
      for (const c of nd.kids) {
        if (!c.r || !c.el) continue;
        const t = openOf(c), leg = smooth(5.5, 7.5, 13 * c.S * T.k), al = a * leg * (1 - smooth(30, 46, 13 * c.S * T.k) * (1 - t));
        const f = t > 0 ? { x: lerp(c.r.x, c.irPad.x, t), y: lerp(c.r.y, c.irPad.y, t), w: lerp(c.r.w, c.irPad.w, t), h: lerp(c.r.h, c.irPad.h, t) } : c.r;
        const x = T.x + T.k * f.x, y = T.y + T.k * f.y;
        if (al < 0.02 || x > E.SW || y > E.SH || x + T.k * f.w < 0 || y + T.k * f.h < 0) continue;
        let inF = !F, anc = false;
        for (let q = c; q && !inF; q = q.parent) inF = q === F;
        for (let q = F && F.parent; q && !anc; q = q.parent) anc = q === c;
        inF = inF || anc;
        const want = inF ? 1 : fd >= 2 || (mm && x + T.k * f.w > mm[0] && y + T.k * f.h > mm[1]) ? 0 : 0.12, wantD = anc && fd >= 2 ? 0 : 1;
        c._dim = c._dim === undefined || fast ? want : c._dim + (want - c._dim) * 0.25;
        c._dec = c._dec === undefined || fast ? wantD : c._dec + (wantD - c._dec) * 0.25;
        if (Math.abs(c._dim - want) > 0.01 || Math.abs(c._dec - wantD) > 0.01) more = true; else { c._dim = want; c._dec = wantD; }
        if (al * c._dim < 0.01 && !inF) continue;
        now.add(c);
        if (c._c.disp !== "") { c.el.style.display = ""; c._c.disp = ""; }
        if (c.fact && t > 0.02) live.current.need(c.fact);
        const sc = t > 0 ? lerp(c.S, c.IS, t) : c.S, rad = (c.frame === "card" || c.frame === "quiet" ? 12 : 8) * sc, key = `${r2(t)}`;
        setA(c, c.el, "opacity", r2(al * c._dim));
        setA(c, c.gs, "opacity:s", r2(1 - t));
        if (c.gi) setA(c, c.gi, "opacity:i", r2(t * c._dec * (1 - smooth(30, 46, 13 * c.IS * T.k))));
        setA(c, c.el, "tabindex", al > 0.5 && t < 0.5 ? "0" : "-1");
        setA(c, c.el, "class", `node f-${c.frame || "card"}${c.L ? "" : " leaf"}${t > 0.5 ? " open" : ""}`);
        if (c._c.geo !== key) {
          c._c.geo = key;
          const box = (el, X, Y, W, H, rx) => { if (!el) return; el.setAttribute("x", r2(X)); el.setAttribute("y", r2(Y)); el.setAttribute("width", r2(Math.max(0, W))); el.setAttribute("height", r2(Math.max(0, H))); el.setAttribute("rx", r2(rx)); };
          box(c.eFr, f.x, f.y, f.w, f.h, rad);
          box(c.eLift, f.x, f.y + 2 * sc, f.w, f.h, rad);
          box(c.eRing, f.x - 3 * sc, f.y - 3 * sc, f.w + 6 * sc, f.h + 6 * sc, rad + 3 * sc);
          box(c.eSt, f.x + 1.5 * sc, f.y + rad * 0.8, 4 * sc, f.h - rad * 1.6, 2 * sc);
        }
        if (c.L && t > 0.02) visit(c, al * t);
      }
    })(R, 1);
    if (more) queue();
    for (const c of E.shown) if (!now.has(c) && c.el) { c.el.style.display = "none"; c._c.disp = "none"; }
    E.shown = now;
    if (mv.current) {
      const w = Math.max(E.world.w * 0.05, E.SW / T.k), h = Math.max(E.world.h * 0.05, E.SH / T.k);
      mv.current.setAttribute("x", r2((E.SW / 2 - T.x) / T.k - w / 2));
      mv.current.setAttribute("y", r2((E.SH / 2 - T.y) / T.k - h / 2));
      mv.current.setAttribute("width", r2(w));
      mv.current.setAttribute("height", r2(h));
    }
    chrome();
  }
  /* Where the view is, for the trail, the headline and the tools. */
  function chrome() {
    // The overview map shows only below the top level, and only where it covers
    // nothing of what is in view.
    const f = focusOf(), R = root(), home = f === R && Math.abs(E.T.k / E.FITK - 1) < 0.03, fr = f !== R && (f.L ? f.irPad : f.r);
    const sx = fr && E.T.x + E.T.k * (fr.x + fr.w), sy = fr && E.T.y + E.T.k * (fr.y + fr.h);
    const miniOn = f !== R && E.SW >= 600 && !(sx > E.SW - 16 - E.mini.w && sy > E.SH - 16 - E.mini.h);
    if (home !== E.home) { E.home = home; set.home(home); }
    if (miniOn !== E.mini.on) { E.mini.on = miniOn; set.mini(miniOn); }
    if (f === E.focus) return;
    E.focus = f;
    queue();
    set.focus(f.id);
    if (f !== R) f.kids.forEach((c) => c.fact && live.current.need(c.fact));
    clearTimeout(E.hashT);
    E.hashT = setTimeout(() => {
      const want = f === R ? "" : `#${HASH}=${encodeURIComponent(f.id)}`;
      if (window.location.hash !== want) window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}${want}`);
    }, 600);
  }

  /* After each layout: find the drawn things, size the map, fit again. */
  function laidOut(lay, first) {
    E.world = lay.world;
    const sc = Math.min(184 / E.world.w, 112 / E.world.h);
    E.mini.w = E.world.w * sc;
    E.mini.h = E.world.h * sc;
    // Each answer rebuilds every node, while React keeps the elements that draw
    // them; what the last frame showed is carried to the nodes those elements
    // draw now, so the next frame hides only what it no longer shows.
    const drawing = new Map();
    dom.nodes.current.querySelectorAll("g.node").forEach((g) => {
      const nd = node(g.dataset.id);
      if (!nd) return;
      const q = (s) => g.querySelector(`:scope > ${s}`);
      Object.assign(nd, { el: g, gs: q(".sum"), gi: q(".inner"), eFr: q(".fr"), eLift: q(".lift"), eSt: q(".stripe"), eRing: q(".ring"), _c: {} });
      drawing.set(g, nd);
    });
    E.shown = new Set([...E.shown].map((c) => drawing.get(c.el)).filter(Boolean));
    E.FITK = fitT(root()).k;
    E.kMax = 4;
    live.current.model.NODES.forEach((nd) => { if (nd.L || nd.S) E.kMax = Math.max(E.kMax, 1.4 / (nd.L ? nd.IS : nd.S)); });
    const fromHash = first && node(new URLSearchParams(window.location.hash.slice(1)).get(HASH) || "");
    const want = fromHash || node(E.target) || root();
    if (first || !E.free) go(want, true); else setT(E.T);
  }
  function resized(w, h) { E.SW = w; E.SH = h; }

  /* Hands: the wheel and a pinch zoom about where they are, a drag pans, a
     click opens what it lands on. */
  function wheel(e) {
    e.preventDefault();
    stopFly();
    E.free = true;
    const r = dom.svg.current.getBoundingClientRect();
    zoomAt(e.clientX - r.left, e.clientY - r.top, E.T.k * 2 ** (-e.deltaY * (e.deltaMode === 1 ? 0.05 : e.deltaMode ? 1 : 0.002) * (e.ctrlKey ? 10 : 1)));
  }
  function pointerDown(e) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    E.ptr.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (E.ptr.size === 1) { E.down = { x: e.clientX, y: e.clientY, moved: false }; window.addEventListener("pointermove", pointerMove); window.addEventListener("pointerup", pointerUp); window.addEventListener("pointercancel", pointerUp); }
  }
  function pointerMove(e) {
    const p = E.ptr.get(e.pointerId);
    if (!p || !E.down) return;
    if (!E.down.moved && Math.hypot(e.clientX - E.down.x, e.clientY - E.down.y) < 5) return;
    if (!E.down.moved) { E.down.moved = true; stopFly(); E.free = true; }
    if (E.ptr.size === 1) setT({ x: E.T.x + e.clientX - p.x, y: E.T.y + e.clientY - p.y, k: E.T.k });
    else if (E.ptr.size === 2) {
      const [o] = [...E.ptr.entries()].filter(([id]) => id !== e.pointerId), q = o[1], r = dom.svg.current.getBoundingClientRect();
      const d0 = Math.hypot(p.x - q.x, p.y - q.y), d1 = Math.hypot(e.clientX - q.x, e.clientY - q.y);
      const m0 = [(p.x + q.x) / 2 - r.left, (p.y + q.y) / 2 - r.top], m1 = [(e.clientX + q.x) / 2 - r.left, (e.clientY + q.y) / 2 - r.top];
      const k1 = clampK(E.T.k * (d0 ? d1 / d0 : 1)), wx = (m0[0] - E.T.x) / E.T.k, wy = (m0[1] - E.T.y) / E.T.k;
      setT({ x: m1[0] - wx * k1, y: m1[1] - wy * k1, k: k1 });
    }
    E.ptr.set(e.pointerId, { x: e.clientX, y: e.clientY });
  }
  function pointerUp(e) {
    E.ptr.delete(e.pointerId);
    if (E.ptr.size) return;
    E.dragged = !!E.down?.moved;
    E.down = null;
    window.removeEventListener("pointermove", pointerMove);
    window.removeEventListener("pointerup", pointerUp);
    window.removeEventListener("pointercancel", pointerUp);
  }
  function click(e) {
    if (E.dragged) { E.dragged = false; return; }
    const l = e.target.closest("[data-ref]");
    if (l) { live.current.follow(l.getAttribute("data-ref")); return; }
    const ed = e.target.closest("g.edge");
    if (ed) { live.current.openEdge(Number(ed.dataset.edge)); return; }
    const g = e.target.closest("g.node"), nd = g && node(g.dataset.id);
    if (nd) go(nd.L ? nd : nd.parent);
  }
  function key(e) {
    if (e.altKey || e.metaKey || e.ctrlKey || e.target.closest("input, select, textarea, .tf-sheet")) return false;
    const on = e.target.closest("g.node, g.edge, [data-ref]");
    if ((e.key === "Enter" || e.key === " ") && on) {
      e.preventDefault();
      if (on.dataset.ref) live.current.follow(on.dataset.ref);
      else if (on.classList.contains("edge")) live.current.openEdge(Number(on.dataset.edge));
      else go(node(on.dataset.id));
      return true;
    }
    if ((e.key === "f" || e.key === "F") && on) { live.current.openProv(on); return true; }
    if (e.target.closest("button") && (e.key === "Enter" || e.key === " ")) return false;
    if (e.key === "Escape") backOut();
    else if (e.key === "+" || e.key === "=") zoomBy(1.6);
    else if (e.key === "-" || e.key === "_") zoomBy(1 / 1.6);
    else if (e.key === "Home" || e.key === "0") go(root());
    else if (e.key.startsWith("Arrow")) { e.preventDefault(); E.free = true; setT({ x: E.T.x + ({ ArrowLeft: 90, ArrowRight: -90 }[e.key] || 0), y: E.T.y + ({ ArrowUp: 90, ArrowDown: -90 }[e.key] || 0), k: E.T.k }); }
    else return false;
    return true;
  }
  const panTo = (r) => { E.free = true; setT({ x: E.SW / 2 - E.T.k * (r.x + r.w / 2), y: E.SH / 2 - E.T.k * (r.y + r.h / 2), k: E.T.k }); };
  return { go, backOut, zoomBy, panTo, laidOut, resized, wheel, pointerDown, click, key, node, state: E, screenOf: (r) => ({ x: E.T.x + E.T.k * r.x, y: E.T.y + E.T.k * r.y, w: E.T.k * r.w, h: E.T.k * r.h }) };
}

/* The fabric's sentence, a hyphenated name never broken across lines. */
const Say = ({ text }) => text.split(/(\S+-\S+)/).map((s, i) => (i % 2 ? <span key={i} className="nb">{s}</span> : s));

/* The customer shown, for a person who sees more than one. */
function CustomerSelect({ customers, customer, onCustomer }) {
  if (customers.length < 2) return null;
  return (
    <label className="tf-customer">Customer{" "}
      <select value={customer} onChange={(e) => onCustomer(e.target.value)}>
        {customers.map((d) => <option key={d.id} value={d.id}>{d.name || d.id}</option>)}
      </select>
    </label>
  );
}

/* The change on trial that waits for a person, soonest first, and the way to it. */
const NeedsBanner = ({ needs, onGo }) => (
  <button type="button" className="tf-needs" onClick={onGo}>
    <span><b>Needs you at {hm(needs.t.decides)}:</b> confirm or undo {needs.t.what || "the change on trial"} at {needs.unit}.{needs.more ? ` ${needs.more === 1 ? "Another box needs" : `${needs.more} more boxes need`} you too.` : ""}</span>
    <span>Go to it ›</span>
  </button>
);

/* The signed fact open in the sheet, and how anything opens its own: a thing
   its newest fact, the others it rests on listed under it; picking one of
   those shows it with the rest. onOpen hears each opening. */
function useFactSheet(need, onOpen) {
  const [sheet, setSheet] = useState(null);
  const close = useCallback(() => setSheet(null), []);
  const openSheet = useCallback((ref, about, others = []) => { need(ref); onOpen?.(); setSheet({ ref, about, others }); }, [need, onOpen]);
  const openThing = useCallback((thing, about) => { if (!thing?.prov) return; const others = (thing.facts || []).map(refOf).filter((f) => f && refKey(f) !== refKey(thing.prov)); openSheet(thing.prov, about, others); }, [openSheet]);
  const pickOther = useCallback((f) => sheet && openSheet(f, sheet.about, [sheet.ref, ...sheet.others.filter((x) => refKey(x) !== refKey(f))]), [sheet, openSheet]);
  return { sheet, close, openSheet, openThing, pickOther };
}

/* The rows of a signed fact, in the sheet. */
function FactSheet({ open, facts, now, onClose, onFollow, onPick }) {
  const held = isHeld(open.ref.name), got = facts.get(refKey(open.ref)), rows = held ? heldRows(open.ref, now) : got?.fact ? factRows(got, now) : null;
  return (
    <aside className="tf-sheet" role="dialog" aria-label={`Signed fact: ${factTitle(open.ref.name)}`}>
      <div className="tf-sheet-head">
        <div>
          <p className="tf-sheet-about">{open.about}</p>
          <h3>{factTitle(open.ref.name)}</h3>
        </div>
        <button type="button" className="tf-icon" onClick={onClose} aria-label="Close the signed fact"><FiX size={16} /></button>
      </div>
      {rows
        ? (
          <dl className="tf-rows">
            {rows.map((r) => (
              <div key={r.label}>
                <dt>{r.label}</dt>
                <dd className={r.mono ? "mono" : ""}>
                  {r.links ? r.links.map((l) => <button type="button" key={l.ref} className="tf-link" onClick={() => onFollow(l.ref)}>→ {l.text}</button>) : r.value}
                </dd>
              </div>
            ))}
          </dl>
        )
        : <p className={got?.error ? "tf-sheet-why" : "tf-quiet"}>{got?.error || "Reading the signed fact from the controller…"}</p>}
      {open.others?.length > 0 && (
        <dl className="tf-rows">
          <dt>It also rests on</dt>
          <dd>{open.others.map((f) => <button type="button" key={refKey(f)} className="tf-link" onClick={() => onPick(f)}>→ {factTitle(f.name)}</button>)}</dd>
        </dl>
      )}
    </aside>
  );
}

function Fabric({ model, facts, need, customers, customer, onCustomer, stale }) {
  const dom = { stage: useRef(null), svg: useRef(null), world: useRef(null), edges: useRef(null), nodes: useRef(null), dots: useRef(null), dot: useRef(null), mv: useRef(null) };
  const head = useRef(null), sayRef = useRef(null), tagHide = useRef(0), rootRef = useRef(null);
  const [size, setSize] = useState(null);
  const [height, setHeight] = useState(MIN_H);
  const [fonts, setFonts] = useState(0);
  const [focusId, setFocusId] = useState("root");
  const [home, setHome] = useState(true);
  const [miniOn, setMiniOn] = useState(false);
  const [tag, setTag] = useState(null);
  const clearTag = useCallback(() => setTag(null), []);
  const { sheet, close: closeSheet, openSheet, openThing, pickOther } = useFactSheet(need, clearTag);
  const [legend, setLegend] = useState(false);
  const [sayOpen, setSayOpen] = useState(false);
  const [clamped, setClamped] = useState(false);
  const live = useRef({});
  const engine = useRef(null);
  if (!engine.current) engine.current = createEngine(dom, { focus: setFocusId, home: setHome, mini: setMiniOn, tag: setTag }, live);
  const en = engine.current;

  const portrait = !!size && (size.w < 760 || size.w / size.h < 0.9);
  // Each layout mutates the model's nodes in place, so it is made only when
  // something it reads changed: the answer, the screen, the facts, the fonts.
  const lay = useMemo(() => (size ? layoutFabric(model, { portrait, width: size.w }, facts) : null), [model, size, portrait, facts, fonts]); // eslint-disable-line react-hooks/exhaustive-deps
  const byFact = useMemo(() => { const m = new Map(); model.NODES.forEach((nd) => nd.fact && m.set(refKey(nd.fact), nd)); return m; }, [model]);
  const follow = useCallback((s) => { const ref = refOf(s), nd = byFact.get(refKey(ref)); if (nd) { closeSheet(); en.go(nd); } else openSheet(ref, "A fact a link names"); }, [byFact, en, openSheet, closeSheet]);
  live.current = {
    model, need, follow,
    openEdge: (i) => { const x = lay?.edges[i]; if (x) openThing(x.e, edgeWords(x.e)); },
    openProv: (el) => { if (el.classList.contains("edge")) live.current.openEdge(Number(el.dataset.edge)); else { const nd = en.node(el.dataset.id); if (nd?.prov) openThing(nd, nd.label); } },
  };

  /* The stage reaches the window's bottom edge; its size drives the layout.
     The view slides in, so its foot is measured again once it has, and the
     console's fonts arrive after the first drawing, which is then measured
     again in them. */
  useLayoutEffect(() => {
    const st = dom.stage.current;
    const fit = () => setHeight(Math.max(MIN_H, Math.floor(toFoot(st))));
    const refont = () => { forgetMeasures(); setFonts((k) => k + 1); };
    const measure = () => { if (st.clientWidth && st.clientHeight) setSize((s) => (s && s.w === st.clientWidth && s.h === st.clientHeight ? s : { w: st.clientWidth, h: st.clientHeight })); };
    fit();
    measure();
    window.addEventListener("resize", fit);
    document.addEventListener("animationend", fit);
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => { fit(); measure(); });
    ro?.observe(st);
    ro?.observe(head.current);
    document.fonts?.ready.then(refont);
    document.fonts?.addEventListener?.("loadingdone", refont);
    return () => {
      window.removeEventListener("resize", fit);
      document.removeEventListener("animationend", fit);
      document.fonts?.removeEventListener?.("loadingdone", refont);
      ro?.disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const first = useRef(true);
  useLayoutEffect(() => {
    if (!lay) return;
    en.resized(size.w, size.h);
    en.laidOut(lay, first.current);
    first.current = false;
  }, [lay]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const svg = dom.svg.current;
    svg.addEventListener("wheel", en.wheel, { passive: false });
    return () => {
      svg.removeEventListener("wheel", en.wheel);
      cancelAnimationFrame(en.state.fly);
      cancelAnimationFrame(en.state.raf);
      clearTimeout(en.state.hashT);
      if (window.location.hash.startsWith(`#${HASH}=`)) window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const focus = model.NODES.get(focusId) || model.root, say = sayOf(focus);
  useLayoutEffect(() => { setSayOpen(false); const el = sayRef.current; setClamped(!!el && el.scrollHeight > el.clientHeight + 2); }, [say, size]);
  const path = [];
  for (let x = focus; x; x = x.parent) path.unshift(x);
  const needs = model.needs;

  /* Who signed what is pointed at: a level-one thing or a line. */
  const point = (el, e) => {
    clearTimeout(tagHide.current);
    const stage = dom.stage.current.getBoundingClientRect();
    if (el.classList.contains("edge")) {
      const x = lay?.edges[Number(el.dataset.edge)];
      if (!x?.e.prov) return;
      const r = el.getBoundingClientRect(), px = e?.clientX ?? r.left + r.width / 2, py = e?.clientY ?? r.top + r.height / 2;
      setTag({ x: px - stage.left + 10, y: py - stage.top + 12, words: provWords(x.e.prov, model.now), open: () => openThing(x.e, edgeWords(x.e)), left: true });
      return;
    }
    const nd = en.node(el.dataset.id);
    if (!nd?.prov || nd.parent !== model.root) return;
    const s = en.screenOf(nd.r);
    setTag({ x: s.x + 12, y: s.y + s.h + 6, words: provWords(nd.prov, model.now), open: () => openThing(nd, nd.label), left: true });
  };
  const leave = () => { clearTimeout(tagHide.current); tagHide.current = setTimeout(() => setTag(null), 300); };
  const hot = (id) => dom.edges.current?.querySelectorAll(".edge.e-spare").forEach((p) => p.classList.toggle("hot", !!id && (p.dataset.a === id || p.dataset.b === id)));
  const over = (e) => { const el = e.target.closest("g.node, g.edge"); if (!el) return; hot(el.dataset.id || null); point(el, e); };
  const focusIn = (e) => {
    const el = e.target.closest("g.node, g.edge");
    if (!el) return;
    hot(el.dataset.id || null);
    point(el);
    const nd = el.dataset.id && en.node(el.dataset.id);
    if (!nd?.r) return;
    const s = en.screenOf(nd.r);
    if ((s.x >= 0 && s.y >= 0 && s.x + s.w <= en.state.SW && s.y + s.h <= en.state.SH) || s.w > en.state.SW || s.h > en.state.SH) return;
    en.panTo(nd.r);
  };
  const keys = useRef(null);
  keys.current = (e) => {
    if (e.target !== document.body && !rootRef.current?.contains(e.target)) return;
    if (e.key === "Escape" && legend) { setLegend(false); return; }
    if (e.key === "Escape" && sheet) { closeSheet(); return; }
    if (en.key(e)) setTag(null);
  };
  useEffect(() => {
    const on = (e) => keys.current(e);
    document.addEventListener("keydown", on);
    return () => document.removeEventListener("keydown", on);
  }, []);

  const nodeEls = useMemo(() => {
    if (!lay) return null;
    const out = [];
    (function walk(nd) {
      for (const c of nd.kids) {
        if (!c.r) continue;
        out.push(
          <g key={c.id} className={`node f-${c.frame || "card"}${c.L ? "" : " leaf"}`} data-id={c.id} role="button" aria-label={`${c.label}. ${sayOf(c)}${c.prov ? ` ${provWords(c.prov, model.now)}; press F for the signed fact.` : ""}`} style={HIDDEN}>
            <rect className="lift" />
            <rect className="fr" />
            {c.stripe && <rect className={`stripe st-${c.stripe}`} />}
            <g className="sum" transform={`translate(${r2(c.r.x)} ${r2(c.r.y)}) scale(${c.S})`}>{c.sum}</g>
            {c.L && <g className="inner" transform={`translate(${r2(c.ir.x)} ${r2(c.ir.y)}) scale(${c.IS})`}>{c.L.decor}</g>}
            <rect className="ring" />
          </g>,
        );
        walk(c);
      }
    })(model.root);
    return out;
  }, [lay]); // eslint-disable-line react-hooks/exhaustive-deps
  const edgeEls = useMemo(() => lay && lay.edges.map((x, i) => (
    <g key={i} className={`edge ${x.cls}`} data-edge={i} data-a={x.hot?.[0]} data-b={x.hot?.[1]} tabIndex={x.cls === "e-spare" ? -1 : 0} role="button" aria-label={`${edgeWords(x.e)}.${x.e.prov ? ` ${provWords(x.e.prov, model.now)}.` : ""}`}>
      <path className="hit" d={x.d} />
      <path className="ln" d={x.d} />
      {x.arrow && <path className="ah" d={x.arrow[2] ? `M${x.arrow[0] - 9} ${x.arrow[1] - 5}L${x.arrow[0] - 9} ${x.arrow[1] + 5}L${x.arrow[0]} ${x.arrow[1]}Z` : `M${x.arrow[0] - 5} ${x.arrow[1] - 9}L${x.arrow[0] + 5} ${x.arrow[1] - 9}L${x.arrow[0]} ${x.arrow[1]}Z`} />}
      {x.label && <text x={r2(x.lx)} y={r2(x.ly)} fontSize="12" fontWeight="600" className="t2 halo">{x.label}</text>}
    </g>
  )), [lay]); // eslint-disable-line react-hooks/exhaustive-deps
  const w = lay?.world, msc = w ? Math.min(184 / w.w, 112 / w.h) : 1;

  return (
    <div className="tf-fabric" ref={rootRef} style={{ "--tf-sans": FONT.sans, "--tf-mono": FONT.mono }}>
      <header className="tf-top" ref={head}>
        <div className="tf-bar">
          <nav className="tf-trail" aria-label="Where you are">
            {path.map((x, i) => (
              <React.Fragment key={x.id}>
                {i > 0 && <i aria-hidden="true">›</i>}
                <button type="button" aria-current={x === focus ? "location" : undefined} onClick={() => x !== focus && en.go(x)}>{x.label}</button>
              </React.Fragment>
            ))}
          </nav>
          {focus.prov && <button type="button" className="tf-prov" onClick={() => openThing(focus, focus.label)}>{provWords(focus.prov, model.now)} ›</button>}
          <span className={`tf-asof${stale ? " is-stale" : ""}`} title={`As of ${asOf(model.now)}`}>As of {hm(model.now)}{stale ? `. ${stale}` : ""}</span>
          <div className="tf-tools">
            <CustomerSelect customers={customers} customer={customer} onCustomer={onCustomer} />
            <button type="button" disabled={home} onClick={en.backOut}>Zoom out</button>
            <button type="button" className="tf-step" aria-label="Zoom out a step" onClick={() => en.zoomBy(1 / 1.6)}>&minus;</button>
            <button type="button" className="tf-step" aria-label="Zoom in a step" onClick={() => en.zoomBy(1.6)}>+</button>
            <button type="button" aria-expanded={legend} aria-controls="tf-legend" onClick={() => setLegend((o) => !o)}>How to read this</button>
          </div>
        </div>
        <div className="tf-head">
          <p ref={sayRef} className={`tf-say${sayOpen ? " open" : ""}`} aria-live="polite">
            <Say text={say} />
          </p>
          {(clamped || sayOpen) && <button type="button" className="tf-more" onClick={() => setSayOpen((o) => !o)}>{sayOpen ? "Less" : "More"}</button>}
        </div>
        {needs && <NeedsBanner needs={needs} onGo={() => en.go(model.NODES.get(needs.to))} />}
      </header>
      <main className="tf-stage" ref={dom.stage} style={{ height }} aria-label="The trust fabric, a canvas you can zoom into">
        <svg className="tf-cv" ref={dom.svg} role="group" aria-label="Your key, the agents, your boxes and their gateways. Zoom into anything to see more."
          onPointerDown={en.pointerDown} onClick={en.click} onPointerOver={over} onPointerOut={leave} onFocus={focusIn} onBlur={leave}>
          <defs><pattern id="tf-dots" ref={dom.dots} patternUnits="userSpaceOnUse" width="24" height="24"><circle ref={dom.dot} cx="12" cy="12" r="0.9" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#tf-dots)" />
          <g ref={dom.world}>
            <g ref={dom.edges}>{lay?.decor}{edgeEls}</g>
            <g ref={dom.nodes}>{nodeEls}</g>
          </g>
        </svg>
        {w && (
          <svg className={`tf-mini${miniOn ? " on" : ""}`} width={r2(w.w * msc)} height={r2(w.h * msc)} viewBox={`${w.x} ${w.y} ${w.w} ${w.h}`} role="button" aria-label="Overview map: where the view sits. Click a thing to go there."
            onClick={(e) => en.go(model.NODES.get(e.target.getAttribute("data-id")) || model.root)}>
            {model.root.kids.filter((x) => x.r).map((x) => <rect key={x.id} x={r2(x.r.x)} y={r2(x.r.y)} width={r2(x.r.w)} height={r2(x.r.h)} rx="10" data-id={x.id} />)}
            <rect ref={dom.mv} className="mv" rx="6" />
          </svg>
        )}
        {tag && (
          <button type="button" className={`tf-tag${tag.left ? " left" : ""}`} style={{ left: Math.max(8, Math.min(tag.x, (size?.w || 0) - 280)), top: Math.max(8, Math.min(tag.y, (size?.h || 0) - 44)) }} onClick={tag.open}
            onPointerEnter={() => clearTimeout(tagHide.current)} onPointerLeave={leave}>
            {tag.words} <span>Open the fact ›</span>
          </button>
        )}
        {legend && (
          <div className="tf-legend" id="tf-legend" role="dialog" aria-label="How to read this">
            <h2>How to read this</h2>
            <ul>
              <li><svg viewBox="0 0 44 14" aria-hidden="true"><g className="e-card"><path className="ln" d="M2 7H38" /><path className="ah" d="M36 2.5 43 7 36 11.5Z" /></g></svg><span>A key card: that agent may change that box now.</span></li>
              <li><svg viewBox="0 0 44 14" aria-hidden="true"><g className="e-tun"><path className="ln" d="M2 7H42" /></g></svg><span>A box’s tunnel to the gateway that countersigns its logbook.</span></li>
              <li><svg viewBox="0 0 44 14" aria-hidden="true"><g className="e-spare"><path className="ln" d="M2 7H42" /></g></svg><span>Another gateway that countersigned it. Shown when you point at a box.</span></li>
              <li><i className="sw ok" /><span>Green: fine, or kept.</span></li>
              <li><i className="sw wait" /><span>Amber: on trial, or waiting for a person.</span></li>
              <li><i className="sw no" /><span>Red: refused, undone, or a logbook that disagrees with ours.</span></li>
              <li><i className="sw idle" /><span>Grey: not heard, or idle.</span></li>
            </ul>
            <p>Scroll, pinch or click a card to look inside. Esc or Zoom out goes back up. Point at a card or a line to see who signed it, and open the signed fact from there.</p>
          </div>
        )}
        {sheet && <FactSheet open={sheet} facts={facts} now={model.now} onClose={closeSheet} onFollow={follow} onPick={pickOther} />}
      </main>
    </div>
  );
}

/* ── The phone layout: the same fabric as a list ───────────────────────── */

/* The things in the canvas's order, under three headings: who may change
   the boxes (the key and the agents), the boxes, their gateways. */
const LIST_SECTIONS = [[["key", "nokey", "agent"], "Who may change your boxes"], [["box"], "Your boxes"], [["gw"], "Weavian gateways"]];
const listId = (id) => `tf-l-${id}`;

/* A tile as a row: a logbook page by its number and time, a step of a trial
   by its time, a check with what it did today, anything else by its title. */
function rowWords(c, now) {
  if (c.page !== undefined) return { title: `${c.label}, ${at(c.t, now)}`, body: c.line };
  if (c.num !== undefined) return { title: c.title, body: c.last };
  if (c.step) return { title: c.time ? `${c.title}, ${atS(c.time, now)}` : c.title, body: c.body };
  return { title: c.title || c.label, body: c.body };
}

function ListRows({ kids, now, onFact }) {
  let question = "";
  return (
    <ul className="tf-lrows">
      {kids.map((c) => {
        const { title, body } = rowWords(c, now);
        const head = c.q && c.q !== question ? (question = c.q) : "";
        const inner = (
          <>
            <span className="tf-lrow-head">
              <span className="tf-lrow-title">{title}</span>
              {c.chip && <span className={`tf-lchip is-${c.chip[0]}`}>{c.chip[1]}</span>}
            </span>
            {body && <span className="tf-lrow-body">{body}</span>}
            {c.fact && <span className="tf-lrow-fact">The signed fact ›</span>}
          </>
        );
        return (
          <React.Fragment key={c.id}>
            {head && <li className="tf-lrows-q">{head}</li>}
            {c.fact
              ? <li><button type="button" className="tf-lrow is-fact" onClick={() => onFact(c.fact, title)}>{inner}</button></li>
              : <li className="tf-lrow">{inner}</li>}
          </React.Fragment>
        );
      })}
    </ul>
  );
}

/* A box as the path a change takes, its stages in order. */
function BoxStages({ nd, now, onFact }) {
  return (
    <ol className="tf-lstages">
      {nd.kids.map((p, i) => (
        <li key={p.id} id={listId(p.id)} className="tf-lstage">
          <h4><span aria-hidden="true">{i + 1}</span>{p.title}</h4>
          <p className="tf-lstage-say">{p.say}</p>
          {p.rows?.length > 0 && (
            <ul className="tf-lrows">
              {p.rows.map((r) => <li key={r.line} className="tf-lrow is-line"><span className={`tf-ldot is-${r.tone}`} aria-hidden="true" />{r.line}</li>)}
            </ul>
          )}
          {p.kids.length > 0 && <ListRows kids={p.kids} now={now} onFact={onFact} />}
        </li>
      ))}
    </ol>
  );
}

/* One thing: what it is in a sentence, a box's lines, who signed it, and
   what it holds behind one disclosure. */
function ListThing({ nd, now, open, onToggle, openThing, onFact }) {
  const box = nd.kind === "box";
  return (
    <article id={listId(nd.id)} className={`tf-lthing${box ? ` is-${nd.pill[0]}` : ""}`}>
      <div className="tf-lthing-head">
        <h4>{nd.title}</h4>
        {box && <span className={`tf-lchip is-${nd.pill[0]}`}>{nd.pill[1]}</span>}
      </div>
      {(nd.role || nd.sub) && <p className="tf-lthing-sub">{nd.role || nd.sub}</p>}
      <p className="tf-lthing-say"><Say text={sayOf(nd)} /></p>
      {box && nd.lines.length > 0 && (
        <ul className="tf-llines" aria-label={`${nd.label}’s lines`}>
          {nd.lines.map((l) => <li key={l.name}><span className={`tf-ldot is-${l.tone}`} aria-hidden="true" />{l.name}{l.brief && <em>{l.brief}</em>}</li>)}
        </ul>
      )}
      {nd.prov && <button type="button" className="tf-prov" onClick={() => openThing(nd, nd.label)}>{provWords(nd.prov, now)} ›</button>}
      {nd.kids.length > 0 && (
        <details className="tf-lmore" open={open} onToggle={(e) => onToggle(nd.id, e.currentTarget.open)}>
          <summary>{box ? `How a change reaches ${nd.label}` : "Look inside"}</summary>
          {box ? <BoxStages nd={nd} now={now} onFact={onFact} /> : <ListRows kids={nd.kids} now={now} onFact={onFact} />}
        </details>
      )}
    </article>
  );
}

/* The fabric on a phone: the same things and signed facts the canvas draws,
   read top to bottom, the answer first; anything resting on a signed fact
   opens it in the sheet. */
function FabricList({ model, facts, need, customers, customer, onCustomer, stale }) {
  const { sheet, close, openSheet, openThing, pickOther } = useFactSheet(need);
  const [open, setOpen] = useState(() => new Set());
  const [goTo, setGoTo] = useState("");
  const toggle = useCallback((id, on) => setOpen((was) => {
    if (was.has(id) === on) return was;
    const next = new Set(was);
    if (on) next.add(id); else next.delete(id);
    return next;
  }), []);
  const follow = useCallback((s) => openSheet(refOf(s), "A fact a link names"), [openSheet]);
  useEffect(() => {
    if (!goTo) return;
    document.getElementById(listId(goTo))?.scrollIntoView({ block: "start", behavior: "smooth" });
    setGoTo("");
  }, [goTo]);
  const needs = model.needs;
  const sections = LIST_SECTIONS.map(([kinds, title]) => [title, model.root.kids.filter((nd) => kinds.includes(nd.kind))]).filter(([, xs]) => xs.length);
  return (
    <div className="tf-list" style={{ "--tf-sans": FONT.sans, "--tf-mono": FONT.mono }}>
      <header className="tf-lhead">
        <div className="tf-lbar">
          <h2>{model.root.label}</h2>
          <span className={`tf-asof${stale ? " is-stale" : ""}`} title={`As of ${asOf(model.now)}`}>As of {hm(model.now)}{stale ? `. ${stale}` : ""}</span>
        </div>
        <CustomerSelect customers={customers} customer={customer} onCustomer={onCustomer} />
        <p className="tf-say" aria-live="polite"><Say text={model.root.say} /></p>
        {needs && <NeedsBanner needs={needs} onGo={() => { toggle(needs.to.split("/")[0], true); setGoTo(needs.to); }} />}
      </header>
      {sections.map(([title, xs]) => (
        <section key={title} className="tf-lsec" aria-label={title}>
          <h3>{title}</h3>
          {xs.map((nd) => <ListThing key={nd.id} nd={nd} now={model.now} open={open.has(nd.id)} onToggle={toggle} openThing={openThing} onFact={openSheet} />)}
        </section>
      ))}
      {model.notDrawn.length > 0 && <p className="tf-lfoot">{notDrawnLine(model)}</p>}
      {sheet && (
        <>
          <div className="tf-lshade" onClick={close} aria-hidden="true" />
          <FactSheet open={sheet} facts={facts} now={model.now} onClose={close} onFollow={follow} onPick={pickOther} />
        </>
      )}
    </div>
  );
}

export default function TrustFabric({ serverApiUrl }) {
  const [phase, setPhase] = useState("loading");
  const [data, setData] = useState(null);
  const [why, setWhy] = useState("");
  const [domains, setDomains] = useState([]);
  const [customer, setCustomer] = useState(remembered);
  const [facts, setFacts] = useState(() => new Map());
  const asked = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const read = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl(`/fabric${customer ? `?customer=${encodeURIComponent(customer)}` : ""}`), { credentials: "same-origin" }, 15000).catch(() => null);
    if (!mounted.current) return;
    const out = res ? await res.json().catch(() => null) : null;
    if (!mounted.current) return;
    // An older server knows no such route and answers 404 in plain text.
    if (res && (res.status === 404 || res.status === 501) && !out?.error) { setPhase("absent"); return; }
    if (!res || !res.ok || !out) {
      setWhy(!res ? "The Weavian server did not answer." : out?.error || `The Weavian server refused (HTTP ${res.status}).`);
      setPhase((p) => (p === "ready" ? p : "error"));
      return;
    }
    setData(out);
    setWhy("");
    setPhase("ready");
  }, [serverApiUrl, customer]);
  useVisibleInterval(read, READ_MS);
  const once = useRef(true);
  useEffect(() => { if (once.current) { once.current = false; return; } read(); }, [read]);
  useEffect(() => {
    fetchWithTimeout(serverApiUrl("/domains"), { credentials: "same-origin" }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      .then((d) => mounted.current && d?.domains && setDomains(d.domains));
  }, [serverApiUrl]);

  const need = useCallback((ref) => {
    const k = refKey(ref);
    if (!ref || isHeld(ref.name) || asked.current.has(k)) return;
    asked.current.add(k);
    readFact(serverApiUrl, ref)
      .catch((e) => ({ error: `This fact could not be read: ${e.message}.` }))
      .then((got) => {
        // One that failed is asked again once it is needed after a while.
        if (got.error) setTimeout(() => asked.current.delete(k), RETRY_MS);
        if (mounted.current) setFacts((m) => new Map(m).set(k, got));
      });
  }, [serverApiUrl]);
  const model = useMemo(() => (data ? buildFabric(data) : null), [data]);
  const pick = (id) => { remember(id); setData(null); setPhase("loading"); setCustomer(id); };
  // A phone reads the fabric as a list; the canvas needs a wider screen to zoom in.
  const phone = usePhoneLayout();
  const View = phone ? FabricList : Fabric;

  const canvas = model && !model.empty && phase !== "absent";
  return (
    <section className={`tf-page${canvas ? "" : " is-padded"}`} aria-labelledby="tf-title">
      <h1 id="tf-title" className="tf-sr-only">Trust Fabric</h1>
      {phase === "absent"
        ? <FeatureUnavailableNotice compact nodeId="this Weavian server" feature="The trust fabric" detail="The server runs a build from before the screen shipped. It appears here once the server is updated; nothing else in the console is affected." />
        : !model
          ? phase === "error"
            ? <div className="tf-note is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{why} The fabric shows here once it answers.</span></div>
            : <p className="tf-note">Reading the trust fabric…</p>
          : model.empty
            ? (
              <div className="tf-empty">
                <h3>Nothing signed yet</h3>
                <p>Boxes appear here once their record reaches the controller, which copies each box’s signed record every five minutes, from its first page.{model.key ? " Your key is in place, so agents may act once a key card names a box." : " Until a key of yours is in place, agents may only read."}</p>
                {model.notDrawn.length > 0 && <p>Not drawn yet: {list(model.notDrawn.map((x) => x.unit))}.</p>}
                <p className="tf-quiet">As of {asOf(model.now)}.</p>
              </div>
            )
            : <View model={model} facts={facts} need={need} customers={domains} customer={data.customer?.id || customer} onCustomer={pick} stale={why ? `${why} This is what it said last.` : ""} />}
    </section>
  );
}
